import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sanitizeString, sanitizeSearchInput } from '../utils/security';
import { fetchServiceRecords, fetchComplaintStats } from '../services/serviceRecordsService';
import ComplaintDetailModal from '../components/ComplaintDetailModal';
import TableListFooter from '../components/TableListFooter';
import { ROWS_PER_PAGE_OPTIONS } from '../constants/pagination';

const safeText = (value) => sanitizeString(value ?? '');

const COMPLAINT_CATEGORIES = [
  { value: '', label: 'Tüm Kategoriler' },
  { value: 'Ürün', label: 'Ürün' },
  { value: 'Bayi', label: 'Bayi' },
  { value: 'Teknik Servis', label: 'Teknik Servis' },
  { value: 'Müşteri Hizmetleri', label: 'Müşteri Hizmetleri' },
  { value: 'Diğer', label: 'Diğer' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Tüm Durumlar' },
  { value: 'Açık', label: 'Açık' },
  { value: 'Çözüldü', label: 'Çözüldü' },
];

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    let date;
    if (dateValue && typeof dateValue === 'object' && dateValue.$date) {
      date = new Date(dateValue.$date);
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return '—';
    }
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return '—';
  }
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';
  let message = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }
  if (message === 'Failed to fetch') return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  return message || fallback;
};

const getStatusBadge = (durum) => {
  const statusColors = {
    'Açık': '#f59e0b',
    'Çözüldü': '#059669',
    'Tamamlandı': '#059669',
  };
  const color = statusColors[durum] || '#64748b';
  return (
    <span style={{
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
      background: `${color}15`,
      color: color,
      border: `1px solid ${color}40`,
    }}>
      {durum || '—'}
    </span>
  );
};

const getTypeBadge = (tur) => {
  const isSikayet = String(tur || '').toLowerCase().includes('şikayet') || String(tur || '').toLowerCase().includes('sikayet');
  const color = isSikayet ? '#dc2626' : '#6366f1';
  const icon = isSikayet ? 'fa-exclamation-circle' : 'fa-lightbulb';
  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
      background: `${color}15`,
      color: color,
      border: `1px solid ${color}40`,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    }}>
      <i className={`fas ${icon}`}></i>
      {tur || '—'}
    </span>
  );
};

const getKategoriBadge = (kategori) => {
  const color = '#475569';
  return (
    <span style={{
      padding: '3px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 500,
      background: `${color}10`,
      color: color,
      border: `1px solid ${color}30`,
    }}>
      {kategori || '—'}
    </span>
  );
};

const getHedefSummary = (record) => {
  const kategori = record?.istekSikayetKategorisi || '';
  const hedefIcerik = record?.hedefIcerik;
  const hedefCihaz = record?.hedefCihaz;
  const hedefTeknikServis = record?.hedefTeknikServis;
  const hedefServisKaydi = record?.hedefServisKaydi;

  if (kategori === 'Ürün') {
    const ad = safeText(hedefIcerik?.icerikAdi || '');
    const seri = safeText(hedefCihaz?.seriNumarasi || '');
    if (ad && seri) return `${ad} · ${seri}`;
    if (ad) return ad;
    if (seri) return `S/N: ${seri}`;
    return '—';
  }
  if (kategori === 'Teknik Servis') {
    const ad = safeText(hedefTeknikServis?.ad || '');
    const numara = safeText(hedefServisKaydi?.numara || '');
    if (ad && numara) return `${ad} · ${numara}`;
    if (ad) return ad;
    if (numara) return numara;
    return '—';
  }
  return '—';
};

const TUR_VALUES = ['Şikayet', 'İstek'];
const DURUM_VALUES = ['', 'Açık', 'Çözüldü'];

const ComplaintRecordsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitRef = useRef(false);
  const turFromUrl = searchParams.get('tur');
  const initialTur = TUR_VALUES.includes(turFromUrl) ? turFromUrl : 'Şikayet';
  const durumFromUrl = searchParams.get('durum') || '';
  const initialDurum = DURUM_VALUES.includes(durumFromUrl) ? durumFromUrl : '';
  const aramaFromUrl = searchParams.get('arama') || '';

  const [activeTab, setActiveTab] = useState(initialTur); // 'Şikayet' (default) | 'İstek'
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const [searchInput, setSearchInput] = useState(aramaFromUrl);
  const [debouncedSearch, setDebouncedSearch] = useState(aramaFromUrl);
  const [filters, setFilters] = useState({
    kategori: '',
    hedefId: '',
    durum: initialDurum,
  });

  const [hedefler, setHedefler] = useState([]);
  const [hedeflerLoading, setHedeflerLoading] = useState(false);

  const [openDetailId, setOpenDetailId] = useState(null);

  const sanitizedSearch = useMemo(() => {
    if (!debouncedSearch || debouncedSearch.trim().length === 0) return '';
    return sanitizeSearchInput(debouncedSearch.trim()).substring(0, 100);
  }, [debouncedSearch]);

  // Search debounce
  useEffect(() => {
    if (searchInput !== debouncedSearch) {
      setSearchLoading(true);
    }
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput.trim()) next.set('arama', searchInput.trim());
        else next.delete('arama');
        return next;
      }, { replace: true });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput, debouncedSearch, setSearchParams]);

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!turFromUrl || !TUR_VALUES.includes(turFromUrl)) next.set('tur', initialTur);
      if (initialDurum) next.set('durum', initialDurum);
      if (aramaFromUrl) next.set('arama', aramaFromUrl);
      return next;
    }, { replace: true });
  }, [turFromUrl, initialTur, initialDurum, aramaFromUrl, setSearchParams]);

  // Hedef listesi (Ürün / Teknik Servis kategorisi seçildiğinde)
  const showHedefSelect = filters.kategori === 'Ürün' || filters.kategori === 'Teknik Servis';

  const loadHedefler = useCallback(async () => {
    if (!showHedefSelect) {
      setHedefler([]);
      return;
    }
    setHedeflerLoading(true);
    try {
      const res = await fetchComplaintStats({ kategori: filters.kategori, tur: activeTab });
      const list = res?.veri?.hedefler || [];
      setHedefler(Array.isArray(list) ? list : []);
    } catch (err) {
      setHedefler([]);
    } finally {
      setHedeflerLoading(false);
    }
  }, [filters.kategori, activeTab, showHedefSelect]);

  useEffect(() => {
    loadHedefler();
  }, [loadHedefler]);

  // Kategori değişince hedef filtresini sıfırla
  useEffect(() => {
    setFilters((prev) => ({ ...prev, hedefId: '' }));
    setPage(1);
  }, [filters.kategori, activeTab]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page,
        limit: rowsPerPage,
        aktif: true,
        islemTipi: 'İstek ve Şikayet',
        istekSikayetTuru: activeTab,
        search: sanitizedSearch || undefined,
      };

      if (filters.kategori) params.istekSikayetKategorisi = filters.kategori;
      if (filters.durum) params.durum = filters.durum;
      if (showHedefSelect && filters.hedefId) {
        if (filters.kategori === 'Ürün') params.hedefIcerikId = filters.hedefId;
        if (filters.kategori === 'Teknik Servis') params.hedefTeknikServisId = filters.hedefId;
      }

      const response = await fetchServiceRecords(params);
      const list = response?.veri?.servisKayitlari || response?.veri || [];
      const pagination = response?.veri?.sayfalama || {};
      setRecords(Array.isArray(list) ? list : []);
      setPaginationMeta({
        total: pagination.toplam || (Array.isArray(list) ? list.length : 0),
        totalPages: pagination.toplamSayfa || Math.max(1, Math.ceil((pagination.toplam || (Array.isArray(list) ? list.length : 0)) / rowsPerPage)),
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Kayıtlar yüklenirken hata oluştu'));
      setRecords([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [page, rowsPerPage, activeTab, sanitizedSearch, filters.kategori, filters.durum, filters.hedefId, showHedefSelect]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tur', tab);
      return next;
    }, { replace: true });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    if (key === 'durum') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('durum', value);
        else next.delete('durum');
        return next;
      }, { replace: true });
    }
  };

  const handleSearchChange = (e) => {
    const value = sanitizeSearchInput(e.target.value, { trimEdges: false }).substring(0, 100);
    setSearchInput(value);
  };

  const handleRowsPerPageChange = (e) => {
    const value = parseInt(e.target.value, 10) || 25;
    setRowsPerPage(value);
    setPage(1);
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Kayıtlar yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }
    if (error) return <div className="error-state">{safeText(error)}</div>;
    if (records.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-comment-dots" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>
            {activeTab === 'Şikayet' ? 'Şikayet kaydı bulunamadı' : 'İstek kaydı bulunamadı'}
          </div>
        </div>
      );
    }
    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>Tarih</th>
            <th>Müşteri</th>
            <th>Kategori</th>
            <th>Tür</th>
            <th>Hedef</th>
            <th>Durum</th>
            <th className="islemler-sutunu">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => {
            const recordId = record.id || record._id;
            const customer = record.musteri || record.musteriId || {};
            const customerName = `${safeText(customer.adi || customer.name || '')} ${safeText(customer.soyadi || customer.surname || '')}`.trim() || '—';
            const customerPhone = safeText(customer.telefon || customer.phone || '');
            return (
              <tr key={recordId}>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(record.olusturmaTarihi || record.createdAt)}</td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 500 }}>{customerName}</span>
                    {customerPhone ? (
                      <span style={{ fontSize: '12px', color: 'var(--text-gray)' }}>{customerPhone}</span>
                    ) : null}
                  </div>
                </td>
                <td>{getKategoriBadge(safeText(record.istekSikayetKategorisi || ''))}</td>
                <td>{getTypeBadge(safeText(record.istekSikayetTuru || ''))}</td>
                <td>
                  <span style={{ fontSize: '13px', color: 'var(--text-dark)' }}>
                    {getHedefSummary(record)}
                  </span>
                </td>
                <td>{getStatusBadge(safeText(record.durum || ''))}</td>
                <td className="table-actions islemler-sutunu">
                  <button
                    type="button"
                    className="category-action-btn edit-btn"
                    onClick={() => setOpenDetailId(recordId)}
                    title="Detayları Görüntüle"
                  >
                    <i className="fas fa-info-circle"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <main className="main-content service-records">
      <div className="content-header">
        <h1>İstek Şikayet Takibi</h1>
      </div>

      {/* Sekmeler: Şikayet (default) / İstek */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        borderBottom: '2px solid var(--border-color)',
        paddingBottom: '0',
      }}>
        <button
          type="button"
          onClick={() => handleTabChange('Şikayet')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'Şikayet' ? '2px solid #dc2626' : '2px solid transparent',
            background: 'transparent',
            color: activeTab === 'Şikayet' ? '#dc2626' : 'var(--text-gray)',
            fontSize: '14px',
            fontWeight: activeTab === 'Şikayet' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px',
          }}
        >
          <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
          Şikayet
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('İstek')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'İstek' ? '2px solid #6366f1' : '2px solid transparent',
            background: 'transparent',
            color: activeTab === 'İstek' ? '#6366f1' : 'var(--text-gray)',
            fontSize: '14px',
            fontWeight: activeTab === 'İstek' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px',
          }}
        >
          <i className="fas fa-lightbulb" style={{ marginRight: '8px' }}></i>
          İstek
        </button>
      </div>

      <div className="toolbar">
        <div className="search-filter-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: '250px' }}>
            {searchLoading ? (
              <span style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'none',
              }}>
                <i className="fas fa-spinner fa-spin" style={{ color: 'var(--text-gray)' }}></i>
              </span>
            ) : (
              <i className="fas fa-search"></i>
            )}
            <input
              type="text"
              placeholder="Müşteri adı, soyadı veya telefon ile ara..."
              value={searchInput}
              onChange={handleSearchChange}
              maxLength={100}
              autoComplete="off"
            />
          </div>

          <select
            value={filters.kategori}
            onChange={(e) => handleFilterChange('kategori', e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#ffffff',
              minWidth: '180px',
            }}
          >
            {COMPLAINT_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          {showHedefSelect && (
            <select
              value={filters.hedefId}
              onChange={(e) => handleFilterChange('hedefId', e.target.value)}
              disabled={hedeflerLoading || hedefler.length === 0}
              style={{
                padding: '10px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#ffffff',
                minWidth: '240px',
                maxWidth: '320px',
              }}
              title={
                hedeflerLoading
                  ? 'Yükleniyor...'
                  : hedefler.length === 0
                  ? 'Bu kategoride veri bulunamadı'
                  : 'En çok şikayet/istek alan üstte'
              }
            >
              <option value="">
                {hedeflerLoading
                  ? 'Yükleniyor...'
                  : hedefler.length === 0
                  ? 'Hedef bulunamadı'
                  : `Tüm ${filters.kategori === 'Ürün' ? 'Ürünler' : 'Teknik Servisler'}`}
              </option>
              {hedefler.map((h) => (
                <option key={h.id} value={h.id}>
                  {`${safeText(h.ad || '—')}${h.kod ? ` (${safeText(h.kod)})` : ''} · ${h.sayi}`}
                </option>
              ))}
            </select>
          )}

          <select
            value={filters.durum}
            onChange={(e) => handleFilterChange('durum', e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#ffffff',
              minWidth: '150px',
            }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#ffffff',
              minWidth: '120px',
            }}
            title="Sayfa başına kayıt"
          >
            <option value={10}>10 / sayfa</option>
            <option value={25}>25 / sayfa</option>
            <option value={50}>50 / sayfa</option>
            <option value={100}>100 / sayfa</option>
          </select>
        </div>
      </div>

      <div className="table-container" style={{ background: 'transparent', border: 'none' }}>
        <div style={{
          background: '#fff',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <div style={{ overflow: 'hidden' }}>{renderTable()}</div>

          <TableListFooter
            totalRecords={paginationMeta.total}
            currentPage={page}
            totalPages={paginationMeta.totalPages || 1}
            onPageChange={setPage}
            loading={loading}
            embedded
          />
        </div>
      </div>

      {openDetailId ? (
        <ComplaintDetailModal
          servisKaydiId={openDetailId}
          onClose={() => setOpenDetailId(null)}
          onResolved={() => {
            loadRecords();
            if (showHedefSelect) loadHedefler();
          }}
        />
      ) : null}
    </main>
  );
};

export default ComplaintRecordsPage;
